import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { ArrowLeft, Key, Plus, Edit2, Trash2, User, Clock, Filter, UserCircle2 } from 'lucide-react';

import { toast } from 'sonner';

const ClientDetail = ({ user, onStatsUpdate }) => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [docs, setDocs] = useState([]);
  const refreshDocs = async () => {
    try {
      const res = await axiosInstance.get(`${API}/clients/${clientId}/documents`);
      setDocs(res.data || []);
    } catch (e) {
      setDocs([]);
    }
  };

  const [showAddNote, setShowAddNote] = useState(false);
  const [noteData, setNoteData] = useState({
    booker_name: '',
    mistake_description: ''
  });
  // weekly notes feature removed

  const [dateFilter, setDateFilter] = useState('all');
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const knownNoteIdsRef = useRef(new Set());
  const hasInitializedNotesRef = useRef(false);

  const playNewNoteSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const gainNode = ctx.createGain();
      gainNode.connect(ctx.destination);
      gainNode.gain.setValueAtTime(0.0001, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);

      const oscillator = ctx.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.connect(gainNode);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.35);

      setTimeout(() => {
        ctx.close();
      }, 450);
    } catch (error) {
      console.error('Unable to play note notification sound:', error);
    }
  };

  const syncNotesAndNotify = (incomingNotes = []) => {
    const nextIds = new Set((incomingNotes || []).map((note) => note.id));

    if (hasInitializedNotesRef.current) {
      const hasNewNote = [...nextIds].some((id) => !knownNoteIdsRef.current.has(id));
      if (hasNewNote) {
        playNewNoteSound();
      }
    }

    knownNoteIdsRef.current = nextIds;
    hasInitializedNotesRef.current = true;
    setNotes(incomingNotes || []);
  };

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  useEffect(() => {
    if (!user || !clientId) return;

    const pollNotes = async () => {
      try {
        const notesRes = await axiosInstance.get(`${API}/notes?client_id=${clientId}`);
        syncNotesAndNotify(notesRes.data || []);
      } catch (error) {
        console.error('Failed to poll notes:', error);
      }
    };

    const intervalId = setInterval(pollNotes, 15000);
    return () => clearInterval(intervalId);
  }, [clientId, user]);

  const fetchClientData = async () => {
    if (!user) return; // Wait for user to be loaded
    try {
      const [clientRes, notesRes] = await Promise.all([
        axiosInstance.get(`${API}/clients/${clientId}`),
        axiosInstance.get(`${API}/notes?client_id=${clientId}`)
      ]);
      setClient(clientRes.data);
      setEditData(clientRes.data);
      syncNotesAndNotify(notesRes.data || []);
      if (user && user.role === 'admin') {
        await refreshDocs();
      }
    } catch (error) {
      console.error('Failed to fetch client data:', error);
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClient = async () => {
    try {
      await axiosInstance.put(`${API}/clients/${clientId}`, editData);
      toast.success('Client updated successfully');
      setEditMode(false);
      fetchClientData();
    } catch (error) {
      console.error('Failed to update client:', error);
      toast.error('Failed to update client');
    }
  };

  const handleAddNote = async () => {
    if (!noteData.mistake_description) {
      toast.error('Please enter note content');
      return;
    }

    try {
      await axiosInstance.post(`${API}/notes`, {
        client_id: clientId,
        booker_name: user?.full_name || '',
        mistake_description: noteData.mistake_description
      });
      toast.success('Note added successfully');
      setShowAddNote(false);
      setNoteData({ booker_name: '', mistake_description: '' });
      fetchClientData();
      onStatsUpdate();
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    }
  };

  const openDeleteNoteDialog = (noteId) => {
    setNoteToDelete(noteId);
    setDeleteNoteDialogOpen(true);
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await axiosInstance.delete(`${API}/notes/${noteToDelete}`);
      toast.success('Note deleted successfully');
      setDeleteNoteDialogOpen(false);
      setNoteToDelete(null);
      fetchClientData();
      onStatsUpdate();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading || !user) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Client not found</p>
        <Button onClick={() => navigate('/')} className="mt-4">Go Back</Button>
      </div>
    );
  }

  const canEdit = user?.role === 'admin' && !client?.is_hidden;
  const isHidden = client?.is_hidden;
  const canAddNote = (user?.role === 'admin' || user?.role === 'leader') && !isHidden;
  
  // Filter notes by date
  const filterNotesByDate = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const threeDays = new Date(today);
    threeDays.setDate(threeDays.getDate() - 3);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    return notes.filter(note => {
      const noteDate = new Date(note.created_at);
      
      switch (dateFilter) {
        case 'today':
          return noteDate >= today;
        case 'yesterday':
          return noteDate >= yesterday && noteDate < today;
        case 'threeDays':
          return noteDate >= threeDays;
        case 'week':
          return noteDate >= lastWeek;
        case 'month':
          return noteDate >= lastMonth;
        case 'all':
        default:
          return true;
      }
    });
  };
  
  const filteredNotes = filterNotesByDate();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button onClick={() => navigate('/')} variant="outline" size="icon" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-2xl font-bold title-gold" data-testid="client-detail-name">{client.client_name}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Info */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Client Information</CardTitle>
            {canEdit && (
              <Button
                onClick={() => setEditMode(!editMode)}
                variant="outline"
                size="sm"
                data-testid="toggle-edit-btn"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                {editMode ? 'Cancel' : 'Edit'}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {editMode ? (
              <>
                <div className="space-y-2">
                  <Label>Client Name</Label>
                  <Input
                    value={editData.client_name}
                    onChange={(e) => setEditData({ ...editData, client_name: e.target.value })}
                    data-testid="edit-client-name-input"
                  />
                </div>
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-medium text-slate-700 mb-2">Misc Notes (Admin Only)</h4>
                  <div className="space-y-2">
                    <Label>Notes for Bookers</Label>
                    <Textarea
                      value={editData.misc_notes || ''}
                      onChange={(e) => setEditData({ ...editData, misc_notes: e.target.value })}
                      placeholder="e.g., State: California, Timezone: PST, Special instructions..."
                      className="min-h-[100px]"
                      data-testid="edit-misc-notes-input"
                    />
                    <p className="text-xs text-slate-500">Visible to all, editable by admins only</p>
                  </div>
                </div>
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-medium text-slate-700 mb-2">Video Introduction</h4>
                  <div className="space-y-2">
                    <Label>Video Introduction URL</Label>
                    <Input
                      type="url"
                      value={editData.video_intro || ''}
                      onChange={(e) => setEditData({ ...editData, video_intro: e.target.value })}
                      placeholder="e.g., https://www.loom.com/share/..."
                      data-testid="edit-video-intro-input"
                    />
                    <p className="text-xs text-slate-500">Add a video introduction link (Loom, YouTube, etc.)</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Calendly Username</Label>
                  <Input
                    value={editData.calendly_username}
                    onChange={(e) => setEditData({ ...editData, calendly_username: e.target.value })}
                    data-testid="edit-calendly-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Calendly Password</Label>
                  <Input
                    value={editData.calendly_password}
                    onChange={(e) => setEditData({ ...editData, calendly_password: e.target.value })}
                    data-testid="edit-calendly-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Calendly Link</Label>
                  <Input
                    value={editData.calendly_link || ''}
                    onChange={(e) => setEditData({ ...editData, calendly_link: e.target.value })}
                    placeholder="www.calendly.com/username"
                    data-testid="edit-calendly-link-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Planet Username</Label>
                  <Input
                    value={editData.planet_username || editData.impact_planet_username || ''}
                    onChange={(e) => setEditData({ ...editData, planet_username: e.target.value })}
                    data-testid="edit-planet-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Planet Password</Label>
                  <Input
                    value={editData.planet_password || editData.impact_planet_password || ''}
                    onChange={(e) => setEditData({ ...editData, planet_password: e.target.value })}
                    data-testid="edit-planet-password-input"
                  />
                </div>
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-medium text-slate-700 mb-2">Booker 1 - IMPACT (Female)</h4>
                  <div className="space-y-2">
                    <Label>Booker 1 Username</Label>
                    <Input
                      value={editData.booker1_impact_username || ''}
                      onChange={(e) => setEditData({ ...editData, booker1_impact_username: e.target.value })}
                      placeholder="Booker 1 username"
                    />
                  </div>
                  <div className="space-y-2 mt-2">
                    <Label>Booker 1 Password</Label>
                    <Input
                      value={editData.booker1_impact_password || ''}
                      onChange={(e) => setEditData({ ...editData, booker1_impact_password: e.target.value })}
                      placeholder="Booker 1 password"
                    />
                  </div>
                </div>
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-medium text-slate-700 mb-2">Booker 2 - IMPACT (Male)</h4>
                  <div className="space-y-2">
                    <Label>Booker 2 Username</Label>
                    <Input
                      value={editData.booker2_impact_username || editData.booker2_username || ''}
                      onChange={(e) => setEditData({ ...editData, booker2_impact_username: e.target.value })}
                      placeholder="Booker 2 username"
                    />
                  </div>
                  <div className="space-y-2 mt-2">
                    <Label>Booker 2 Password</Label>
                    <Input
                      value={editData.booker2_impact_password || editData.booker2_password || ''}
                      onChange={(e) => setEditData({ ...editData, booker2_impact_password: e.target.value })}
                      placeholder="Booker 2 password"
                    />
                  </div>
                </div>
                <Button onClick={handleUpdateClient} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-client-btn">
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                {/* Misc Notes - Always visible */}
                {client.misc_notes && (
                  <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="font-medium text-slate-700 mb-1">Misc Notes {!canEdit && "(Admin Only)"}</p>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap" data-testid="detail-misc-notes">{client.misc_notes}</p>
                    </div>
                  </div>
                )}

                {client.video_intro && (
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <svg className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="font-medium text-slate-700 mb-1">Video Introduction</p>
                      <a 
                        href={client.video_intro} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="text-sm text-blue-600 underline break-all hover:text-blue-800"
                        data-testid="detail-video-intro"
                      >
                        {client.video_intro}
                      </a>
                    </div>
                  </div>
                )}
                
                {(client.calendly_username || client.calendly_password || client.calendly_link) && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_client-tracker-121/artifacts/oyxw054i_Calendly-New-Logo.png" 
                      alt="Calendly" 
                      className="h-8 w-8 mt-0.5 object-contain"
                    />
                    <div>
                      <p className="font-medium text-slate-700 mb-1">Calendly Account</p>
                      {/* Username is only returned to admins; backend redacts for others */}
                      {client.calendly_username && (
                        <p className="text-sm text-slate-600" data-testid="detail-calendly-username">Username: {client.calendly_username}</p>
                      )}
                      {client.calendly_link && (
                        <p className="text-sm text-blue-600 underline">
                          <a
                            href={client.calendly_link.startsWith('http') ? client.calendly_link : `https://${client.calendly_link}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="detail-calendly-link"
                          >
                            Link: {client.calendly_link.startsWith('http') ? client.calendly_link : `https://${client.calendly_link}`}
                          </a>
                        </p>
                      )}
                      {/* Calendly password is redacted for non-admins by backend; show only if present (admin) */}
                      {client.calendly_password && (
                        <p className="text-sm text-slate-600" data-testid="detail-calendly-password">Password: {client.calendly_password}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {(client.planet_username || client.impact_planet_username || client.planet_password || client.impact_planet_password || client.planet_mobile_link) && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Key className="h-5 w-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700 mb-1">Planet Account</p>
                      {(client.planet_username || client.impact_planet_username) && (
                        <p className="text-sm text-slate-600" data-testid="detail-planet-username">Username: {client.planet_username || client.impact_planet_username}</p>
                      )}
                      {(client.planet_password || client.impact_planet_password) && (
                        <p className="text-sm text-slate-600" data-testid="detail-planet-password">Password: {client.planet_password || client.impact_planet_password}</p>
                      )}

                      {client.planet_mobile_link && (
                        <p className="text-sm text-blue-600 underline">
                          <a
                            href={client.planet_mobile_link.startsWith('http') ? client.planet_mobile_link : `https://${client.planet_mobile_link}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="detail-planet-mobile-link"
                          >
                            Planet Mobile: {client.planet_mobile_link.startsWith('http') ? client.planet_mobile_link : `https://${client.planet_mobile_link}`}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Show IMPACT Mobile link only if both Booker 1 and Booker 2 fields are present */}
                {( (client.booker1_impact_username || client.booker1_impact_password) && (client.booker2_impact_username || client.booker2_impact_password || client.booker2_username || client.booker2_password) ) && (
                  <div className="p-2 flex items-center gap-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_client-tracker-121/artifacts/jejboty7_GL-9c593a3d.png" 
                      alt="IMPACT" 
                      className="h-6 w-6 object-contain"
                    />
                    <a
                      href="https://sso.ailife.com/ImpactPortal/SecureAuth.aspx?client_id=a97eeaf1c52946e4a438a67fb95061f0&redirect_uri=https%3A%2F%2Fimpact.ailife.com%2Fsignin&response_type=code%20id_token&scope=openid%20profile%20offline_access&response_mode=form_post&nonce=638956410121602807.M2ViODIxMDUtYzJhNy00N2IwLTg2ZDgtNWJjMmE0M2M1M2QzZjU0ODUzZGItMTM5My00MTIxLWFkZGItZmE0MDI0YTVkZmE5&state=client-tracker-121&x-client-SKU=ID_NET8_0&x-client-ver=7.1.2.0"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 font-semibold underline break-all"
                      data-testid="detail-impact-mobile-link"
                    >
                      Open IMPACT Mobile
                    </a>
                  </div>
                )}


                {/* Open Planet Mobile above Open IMPACT Mobile if Planet creds exist */}
                {((client.planet_username || client.impact_planet_username) && (client.planet_password || client.impact_planet_password)) && (
                  <div className="p-2 flex items-center gap-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_client-tracker-121/artifacts/9of3ndc3_20240408015249_600x600_ao_logo.png" 
                      alt="Planet" 
                      className="h-6 w-6 object-contain"
                    />
                    <a
                      href="https://m.planetaltig.com/Account/Login"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 font-semibold underline break-all"
                      data-testid="detail-planet-mobile-static-link"
                    >
                      Open Planet Mobile
                    </a>
                  </div>
                )}

                {(client.booker1_impact_username || client.booker1_impact_password) && (
                  <div className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg border border-pink-200">
                    <svg className="h-5 w-5 text-pink-600 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                      {/* Female symbol */}
                      <path d="M12 2a5 5 0 0 0-5 5c0 2.47 1.79 4.53 4.15 4.94v2.06h-2v2h2v2h2v-2h2v-2h-2v-2.06A5.002 5.002 0 0 0 12 2zm0 2c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z"/>
                    </svg>
                    <div>
                      <p className="font-medium text-slate-700 mb-1">Booker 1 - IMPACT (Female)</p>
                      {client.booker1_impact_username && (
                        <p className="text-sm text-slate-600">Username: {client.booker1_impact_username}</p>
                      )}
                      {client.booker1_impact_password && (
                        <p className="text-sm text-slate-600">Password: {client.booker1_impact_password}</p>
                      )}
                    </div>
                  </div>
                )}

                
                {(client.booker2_impact_username || client.booker2_impact_password || client.booker2_username || client.booker2_password) && (
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <svg className="h-5 w-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                      {/* Male symbol */}
                      <path d="M15.5 2h6v6h-2V5.41l-4.3 4.3a5 5 0 1 1-1.41-1.42l4.3-4.29H15.5V2zM10 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>
                    </svg>
                    <div>
                      <p className="font-medium text-slate-700 mb-1">Booker 2 - IMPACT (Male)</p>
                      {(client.booker2_impact_username || client.booker2_username) && (
                        <p className="text-sm text-slate-600">Username: {client.booker2_impact_username || client.booker2_username}</p>
                      )}
                      {(client.booker2_impact_password || client.booker2_password) && (
                        <p className="text-sm text-slate-600">Password: {client.booker2_impact_password || client.booker2_password}</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Docs (Admin) */}
        {user?.role === 'admin' && (
          <Card>
            <CardHeader className="space-y-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl">Docs ({docs.length}/10)</CardTitle>
                <div className="flex items-center gap-3">
                  {docs.length >= 10 && (
                    <span className="text-xs text-amber-600">Limit reached (10 files)</span>
                  )}
                  <input
                    type="file"
                    disabled={docs.length >= 10}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        // Check if we need to prompt for append/replace
                        const list = await axiosInstance.get(`${API}/clients/${clientId}/documents`);
                        const exists = (list.data || []).some((d) => d.filename === file.name);
                        let action = undefined;
                        if (exists) {
                          action = window.confirm('File exists. Click OK to replace, Cancel to append a suffix.') ? 'replace' : 'append';
                        }
                        const formData = new FormData();
                        formData.append('file', file);
                        if (action) formData.append('action', action);
                        await axiosInstance.post(`${API}/clients/${clientId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        e.target.value = '';
                        await refreshDocs();
                      } catch (err) {
                        alert(err.response?.data?.detail || 'Upload failed');
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {docs.length === 0 ? (
                <div className="text-slate-600">No documents uploaded yet.</div>
              ) : (
                <ul className="divide-y">
                  {docs.map((d) => (
                    <li key={d.id} className="py-2 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-800">{d.filename}</div>
                        <div className="text-xs text-slate-500">{Math.round(d.length / 1024 / 1024 * 10) / 10} MB • {new Date(d.uploaded_at).toLocaleString()} • {d.uploaded_by || 'admin'}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="btn-gold-outline"
                          onClick={async () => {
                            try {
                              const res = await axiosInstance.get(`${API}/clients/${clientId}/documents/${d.id}`, { responseType: 'blob' });
                              const ctype = res.headers['content-type'] || d.contentType || 'application/octet-stream';
                              const blob = new Blob([res.data], { type: ctype });
                              const url = window.URL.createObjectURL(blob);
                              // Always trigger a safe download to avoid Edge popup blocks
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = d.filename || 'download';
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              setTimeout(() => window.URL.revokeObjectURL(url), 2000);
                              toast.success('Download started');
                            } catch (err) {
                              const msg = err?.response?.data?.detail || 'Download failed';
                              toast.error(msg);
                              console.error('Download error:', err);
                            }
                          }}
                        >
                          Download
                        </Button>
                        <Button variant="destructive" className="btn-red" onClick={async () => {
                          if (!window.confirm('Delete this document?')) return;
                          try {
                            await axiosInstance.delete(`${API}/clients/${clientId}/documents/${d.id}`);
                            toast.success('Document deleted');
                            await refreshDocs();
                          } catch (e) {
                            toast.error('Delete failed');
                            console.error('Delete error:', e);
                          }
                        }}>Delete</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {/* Docs List */}
        {/* Removed duplicate Docs card */}


        {/* Notes Section */}
        <Card>
          <CardHeader className="space-y-4">
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-2xl">Notes</CardTitle>
              {canAddNote && (
              <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-note-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Note
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Manager Note</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Notes:</Label>
                      <Textarea
                        placeholder="Describe recommended practices and guidelines to follow for this manager..."
                        value={noteData.mistake_description}
                        onChange={(e) => setNoteData({ ...noteData, mistake_description: e.target.value })}
                        rows={8}
                        data-testid="note-description-input"
                        className="min-h-[200px]"
                      />
                    </div>
                    <Button onClick={handleAddNote} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-note-btn">
                      Add Note
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            </div>
            
            {/* Filter by Date */}
            {notes.length > 0 && (
              <div className="flex items-center gap-3">
                <Filter className="h-4 w-4 text-slate-500" />
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="date-filter-select">
                    <SelectValue placeholder="All time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="threeDays">Last 3 days</SelectItem>
                    <SelectItem value="week">Last 7 days</SelectItem>
                    <SelectItem value="month">Last 30 days</SelectItem>
                  </SelectContent>
                </Select>
                {dateFilter !== 'all' && (
                  <span className="text-sm text-slate-600">
                    Showing {filteredNotes.length} of {notes.length} notes
                  </span>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {notes.length === 0 ? (
              <div className="text-center py-8 text-slate-600" data-testid="no-notes-msg">
                <p>No notes yet</p>
                {canAddNote && <p className="text-sm mt-2">Add the first note to document do's and don'ts</p>}
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="notes-list">
                {filteredNotes.map((note) => (
                  <div key={note.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid={`note-item-${note.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      {user?.role === 'admin' && (
                        <Button
                          onClick={() => openDeleteNoteDialog(note.id)}
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 ml-auto"
                          data-testid={`delete-note-btn-${note.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mb-2" data-testid={`note-description-${note.id}`}>{note.mistake_description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(note.created_at)}</span>
                      </div>
                      <span>Added by: {note.created_by_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Note Confirmation Dialog */}
      <AlertDialog open={deleteNoteDialogOpen} onOpenChange={setDeleteNoteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setNoteToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteNote} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientDetail;